import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Mail, MessageSquare, Loader2, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent, trackFormError } from "@/components/GoogleAnalytics";

interface QuickContactFormProps {
  variant?: "default" | "compact";
}

const QuickContactForm = ({ variant = "default" }: QuickContactFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.service) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      trackFormError('quick_contact', 'missing_required_fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      trackFormError('quick_contact', 'invalid_email');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use secure edge function for submission
      const { data, error } = await supabase.functions.invoke('submit-intake-request', {
        body: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || null,
          service: formData.service,
          message: formData.message.trim() || null
        }
      });

      if (error) {
        console.error('Submission error:', error);
        throw new Error(error.message || 'Failed to submit request');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to submit request');
      }

      // Track successful contact form submission
      trackEvent('contact_form_submitted', {
        category: 'conversion',
        label: 'quick_contact_form',
        custom_parameters: {
          service_type: formData.service
        }
      });
      
      setIsSubmitted(true);
      
      toast({
        title: "Message Sent!",
        description: "We'll contact you within 24 hours to discuss your project.",
      });

      // Reset form after short delay
      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          phone: "",
          service: "",
          message: ""
        });
        setIsSubmitted(false);
      }, 3000);

    } catch (error: any) {
      console.error("Quick contact form error:", error);
      
      trackFormError('quick_contact', error.message || 'submission_failed');
      
      toast({
        title: "Submission Error",
        description: error?.message || "Failed to send message. Please try calling us at 616.727.0343.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    { value: "home-organization", label: "Home Organization" },
    { value: "cleaning-housekeeping", label: "Cleaning & Housekeeping" },
    { value: "office-organization", label: "Office Organization" },
    { value: "decluttering", label: "Decluttering" },
    { value: "moving-prep", label: "Moving Preparation" },
    { value: "estate-organization", label: "Estate Organization" },
    { value: "adhd-systems", label: "ADHD-Friendly Systems" },  
    { value: "real-estate-prep", label: "Real Estate Staging" },
    { value: "consultation", label: "Free Consultation" }
  ];

  if (variant === "compact") {
    return (
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center">
            {isSubmitted ? (
              <>
                <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                Message Sent!
              </>
            ) : (
              "Quick Contact"
            )}
          </CardTitle>
          <CardDescription>
            {isSubmitted ? "We'll be in touch within 24 hours" : "Get a response within 24 hours"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isSubmitted ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Thank you! We'll contact you soon.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                placeholder="Your name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                minLength={2}
                className="h-10"
              />
              <Input
                type="email"
                placeholder="Email address *"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-10"
              />
              <Input
                type="tel"
                placeholder="Phone number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="h-10"
              />
              <Select
                value={formData.service}
                onValueChange={(value) => setFormData({ ...formData, service: value })}
                required
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select service *" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.value} value={service.value}>
                      {service.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                type="submit" 
                className="w-full luxury-gradient text-primary-foreground h-11"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Get Free Quote
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto shadow-lg">
      <CardHeader>
        <CardTitle className="text-2xl text-center flex items-center justify-center">
          {isSubmitted ? (
            <>
              <CheckCircle className="w-6 h-6 mr-2 text-green-500" />
              Request Submitted!
            </>
          ) : (
            "Get Your Free Consultation"
          )}
        </CardTitle>
        <CardDescription className="text-center text-lg">
          {isSubmitted 
            ? "We'll contact you within 24 hours to discuss your project" 
            : "Tell us about your project and we'll provide a custom quote within 24 hours"
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isSubmitted ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Thank you for your request!</h3>
            <p className="text-muted-foreground mb-6">
              We've received your information and will be in touch within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="outline" 
                onClick={() => window.open('tel:+16167270343')}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.open('mailto:hello@organizebydesignmichigan.com')}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Us
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Full Name *</label>
                <Input
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  minLength={2}
                  className="h-11"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Email Address *</label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Phone Number</label>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-11"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Service Needed *</label>
                <Select
                  value={formData.service}
                  onValueChange={(value) => setFormData({ ...formData, service: value })}
                  required
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.value} value={service.value}>
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Tell us about your project</label>
              <Textarea
                placeholder="Describe your space, goals, timeline, and any specific challenges..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                className="resize-none"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                type="submit" 
                size="lg"
                className="luxury-gradient text-primary-foreground px-8 h-12"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending Request...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    Get Free Consultation
                  </>
                )}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="lg"
                className="px-8 h-12"
                onClick={() => window.open('tel:+16167270343')}
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 616.727.0343
              </Button>
            </div>

            <p className="text-sm text-center text-muted-foreground">
              We respect your privacy and will never share your information.
            </p>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default QuickContactForm;